"use client";

import { Trans } from "@lingui/react/macro";
import { graphql } from "gql.tada";
import { HomeIcon } from "lucide-react";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import React from "react";
import { useQuery } from "urql";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { isType } from "@/utils/gql-type-utils";
import { getPath } from "./breadcrumb-utils";

type Props = {
  lang: string;
  workspaceId: string;
};

const NodeNameQuery = graphql(`
  query NodeNameQuery($id: ID!) {
    node(id: $id) {
      id
      __typename
      ... on IndexWorkerProject {
        name
      }
      ... on SearchAnalyticsProject {
        id
        name
      }
      ... on UserAnalyticsProject {
        id
        name
      }
      ... on AdsAnalyticsProject {
        id
        name
      }
      ... on IndexWorker {
        name
      }
      ... on SearchAnalyticsView {
        name
      }
      ... on UserAnalyticsView {
        name
      }
      ... on AdsAnalyticsView {
        name
      }
      ... on Dashboard {
        name
      }
    }
  }
`);

export function WorkspaceBreadcrumb({ lang, workspaceId }: Props) {
  const path = getPath(usePathname(), useParams());

  const [
    { data: projectData, fetching: projectFetching, stale: projectStale },
  ] = useQuery({
    query: NodeNameQuery,
    variables: {
      id:
        path.type === "iw"
          ? path.projectId
          : path.type === "sa"
            ? path.projectId
            : path.type === "ua"
              ? path.projectId
              : path.type === "ads"
                ? path.projectId
                : "",
    },
    pause:
      path.type !== "iw" &&
      path.type !== "sa" &&
      path.type !== "ua" &&
      path.type !== "ads",
  });
  const projectName =
    path.type === "iw" ||
    path.type === "sa" ||
    path.type === "ua" ||
    path.type === "ads"
      ? isType(projectData?.node, "IndexWorkerProject") ||
        isType(projectData?.node, "SearchAnalyticsProject") ||
        isType(projectData?.node, "UserAnalyticsProject") ||
        isType(projectData?.node, "AdsAnalyticsProject")
        ? projectData.node.name
        : null
      : null;

  const [
    {
      data: indexWorkerData,
      fetching: indexWorkerFetching,
      stale: indexWorkerStale,
    },
  ] = useQuery({
    query: NodeNameQuery,
    variables: {
      id: path.type === "iw" ? (path.indexWorkerId ?? "") : "",
    },
    pause: path.type !== "iw" || !path.indexWorkerId,
  });
  const indexWorkerName =
    path.type === "iw"
      ? isType(indexWorkerData?.node, "IndexWorker")
        ? indexWorkerData.node.name
        : null
      : null;
  const [
    {
      data: searchAnalyticsViewData,
      fetching: searchAnalyticsViewFetching,
      stale: searchAnalyticsViewStale,
    },
  ] = useQuery({
    query: NodeNameQuery,
    variables: {
      id: path.type === "sa" ? (path.viewId ?? "") : "",
    },
    pause: path.type !== "sa" || !path.viewId,
  });
  const searchAnalyticsViewName =
    path.type === "sa"
      ? isType(searchAnalyticsViewData?.node, "SearchAnalyticsView")
        ? searchAnalyticsViewData.node.name
        : null
      : null;
  const [
    {
      data: userAnalyticsViewData,
      fetching: userAnalyticsViewFetching,
      stale: userAnalyticsViewStale,
    },
  ] = useQuery({
    query: NodeNameQuery,
    variables: {
      id: path.type === "ua" ? (path.viewId ?? "") : "",
    },
    pause: path.type !== "ua" || !path.viewId,
  });
  const userAnalyticsViewName =
    path.type === "ua"
      ? isType(userAnalyticsViewData?.node, "UserAnalyticsView")
        ? userAnalyticsViewData.node.name
        : null
      : null;
  const [
    { data: adsViewData, fetching: adsViewFetching, stale: adsViewStale },
  ] = useQuery({
    query: NodeNameQuery,
    variables: {
      id: path.type === "ads" ? (path.viewId ?? "") : "",
    },
    pause: path.type !== "ads" || !path.viewId,
  });
  const adsViewName =
    path.type === "ads"
      ? isType(adsViewData?.node, "AdsAnalyticsView")
        ? adsViewData.node.name
        : null
      : null;
  const [
    { data: dashboardData, fetching: dashboardFetching, stale: dashboardStale },
  ] = useQuery({
    query: NodeNameQuery,
    variables: {
      id: path.type === "dashboards" ? (path.dashboardId ?? "") : "",
    },
    pause: path.type !== "dashboards",
  });
  const dashboardName =
    path.type === "dashboards"
      ? isType(dashboardData?.node, "Dashboard")
        ? dashboardData.node.name
        : null
      : null;

  return (
    <Breadcrumb>
      <BreadcrumbList className="flex-nowrap">
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link href={`/${lang}/${encodeURIComponent(workspaceId)}`}>
              <HomeIcon className="size-4" />
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {path.type !== "home" && <BreadcrumbSeparator />}

        {/* Index Worker の実行計画 */}
        {path.type === "iw-job-plan" && (
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link
                href={`/${lang}/${encodeURIComponent(workspaceId)}/iw/job-plan`}
              >
                <Trans>Job execution plan</Trans>
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
        )}

        {/* Index Worker */}
        {path.type === "iw" && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink
                asChild
                className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
              >
                <Link
                  href={`/${lang}/${encodeURIComponent(workspaceId)}/iw/${encodeURIComponent(path.projectId)}`}
                >
                  {path.indexWorkerId || path.isSettings ? (
                    <EllipsisOnMobile>
                      <NameOrSkeleton
                        name={projectName}
                        isLoading={projectFetching || projectStale}
                      />
                    </EllipsisOnMobile>
                  ) : (
                    <NameOrSkeleton
                      name={projectName}
                      isLoading={projectFetching || projectStale}
                    />
                  )}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {indexWorkerName && path.indexWorkerId && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
                  >
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/iw/${encodeURIComponent(path.projectId)}/${encodeURIComponent(path.indexWorkerId)}`}
                    >
                      <NameOrSkeleton
                        name={indexWorkerName}
                        isLoading={indexWorkerFetching || indexWorkerStale}
                      />
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isSettings && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/iw/${encodeURIComponent(path.projectId)}/settings`}
                    >
                      <Trans>Settings</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
          </>
        )}

        {/* Search Analytics */}
        {path.type === "sa" && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink
                asChild
                className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
              >
                <Link
                  href={`/${lang}/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(path.projectId)}`}
                >
                  {path.viewId ||
                  path.isSettings ||
                  path.isDefaultView ||
                  path.isOnboarding ? (
                    <EllipsisOnMobile>
                      <NameOrSkeleton
                        name={projectName}
                        isLoading={projectFetching || projectStale}
                      />
                    </EllipsisOnMobile>
                  ) : (
                    <NameOrSkeleton
                      name={projectName}
                      isLoading={projectFetching || projectStale}
                    />
                  )}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {((searchAnalyticsViewName && path.viewId) ||
              path.isDefaultView) && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
                  >
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(path.projectId)}/${path.isDefaultView ? "default" : encodeURIComponent(path.viewId ?? "")}`}
                    >
                      {path.isDefaultView ? (
                        <Trans id="default-view.name">All</Trans>
                      ) : (
                        <NameOrSkeleton
                          name={searchAnalyticsViewName}
                          isLoading={
                            searchAnalyticsViewFetching ||
                            searchAnalyticsViewStale
                          }
                        />
                      )}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isSettings && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
                  >
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(path.projectId)}/settings`}
                    >
                      <Trans>Settings</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isOnboarding && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
                  >
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/sa/${encodeURIComponent(path.projectId)}/onboarding`}
                    >
                      <Trans>Initial analysis</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
          </>
        )}

        {/* User Analytics */}
        {path.type === "ua" && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink
                asChild
                className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
              >
                <Link
                  href={`/${lang}/${encodeURIComponent(workspaceId)}/ua/${encodeURIComponent(path.projectId)}`}
                >
                  {path.viewId ||
                  path.isSettings ||
                  path.isDefaultView ||
                  path.isSessionExplorer ? (
                    <EllipsisOnMobile>
                      <NameOrSkeleton
                        name={projectName}
                        isLoading={projectFetching || projectStale}
                      />
                    </EllipsisOnMobile>
                  ) : (
                    <NameOrSkeleton
                      name={projectName}
                      isLoading={projectFetching || projectStale}
                    />
                  )}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {((userAnalyticsViewName && path.viewId) || path.isDefaultView) && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
                  >
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/ua/${encodeURIComponent(path.projectId)}/${path.isDefaultView ? "default" : encodeURIComponent(path.viewId ?? "")}`}
                    >
                      {path.isDefaultView ? (
                        <Trans id="default-view.name">All</Trans>
                      ) : (
                        <NameOrSkeleton
                          name={userAnalyticsViewName}
                          isLoading={
                            userAnalyticsViewFetching || userAnalyticsViewStale
                          }
                        />
                      )}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isSessionExplorer && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/ua/${encodeURIComponent(path.projectId)}/session_explorer`}
                    >
                      <Trans>Session Explorer</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isSettings && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/ua/${encodeURIComponent(path.projectId)}/settings`}
                    >
                      <Trans>Settings</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
          </>
        )}

        {/* Ads Analytics */}
        {path.type === "ads" && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink
                asChild
                className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
              >
                <Link
                  href={`/${lang}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(path.projectId)}`}
                >
                  {path.viewId || path.isSettings || path.isDefaultView ? (
                    <EllipsisOnMobile>
                      <NameOrSkeleton
                        name={projectName}
                        isLoading={projectFetching || projectStale}
                      />
                    </EllipsisOnMobile>
                  ) : (
                    <NameOrSkeleton
                      name={projectName}
                      isLoading={projectFetching || projectStale}
                    />
                  )}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {((adsViewName && path.viewId) || path.isDefaultView) && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
                  >
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(path.projectId)}/${path.isDefaultView ? "default" : encodeURIComponent(path.viewId ?? "")}`}
                    >
                      {path.isDefaultView ? (
                        <Trans id="default-view.name">All</Trans>
                      ) : (
                        <NameOrSkeleton
                          name={adsViewName}
                          isLoading={adsViewFetching || adsViewStale}
                        />
                      )}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isSettings && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/ads/${encodeURIComponent(path.projectId)}/settings`}
                    >
                      <Trans>Settings</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
          </>
        )}

        {/* Dashboards */}
        {path.type === "dashboards" && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink
                asChild
                className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate"
              >
                <Link
                  href={`/${lang}/${encodeURIComponent(workspaceId)}/dashboards/${encodeURIComponent(path.dashboardId)}`}
                >
                  <NameOrSkeleton
                    name={dashboardName}
                    isLoading={dashboardFetching || dashboardStale}
                  />
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          </>
        )}

        {/* Inbox */}
        {path.type === "inbox" && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  href={`/${lang}/${encodeURIComponent(workspaceId)}/inbox`}
                >
                  <Trans>Inbox</Trans>
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          </>
        )}

        {path.type === "artifacts" && (
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link
                href={`/${lang}/${encodeURIComponent(workspaceId)}/artifacts`}
              >
                <Trans>AI Artifacts</Trans>
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
        )}
        {/* Amethyst AI */}
        {path.type === "ai" && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href={`/${lang}/${encodeURIComponent(workspaceId)}/ai`}>
                  {"Amethyst AI"}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
          </>
        )}

        {/* Settings */}
        {path.type === "settings" && (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link
                  href={`/${lang}/${encodeURIComponent(workspaceId)}/settings`}
                >
                  <Trans>Settings</Trans>
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {path.isSCProperties && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/settings/sc-properties`}
                    >
                      <Trans>Search Console properties</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isNotifications && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/settings/notifications`}
                    >
                      <Trans>Workspace notification settings</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isSABigQuery && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/settings/sa-bigquery`}
                    >
                      <Trans>Search Analytics BigQuery</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isUABigQuery && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/settings/ua-bigquery`}
                    >
                      <Trans>User Analytics BigQuery</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            {path.isWorkflowSkills && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link
                      href={`/${lang}/${encodeURIComponent(workspaceId)}/settings/workflow-skills`}
                    >
                      <Trans>Skills</Trans>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * 名前が取得済みなら即座に表示し、未取得の場合のみスケルトンを出すコンポーネント。
 * 再検証中でもキャッシュ済みの値を優先し、ローディング表示の固定化を防ぐ。
 */
function NameOrSkeleton({
  name,
  isLoading,
}: {
  name: React.ReactNode | null;
  isLoading: boolean;
}) {
  if (name) return <>{name}</>;
  if (isLoading) return <Skeleton className="w-20 h-4" />;
  return null;
}

/**
 * モバイルでは省略記号を表示する。
 * デスクトップではテキストを表示し、長い場合は省略する。
 */
function EllipsisOnMobile({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <BreadcrumbEllipsis />;
  }

  return (
    <span className="max-w-40 lg:max-w-80 xl:max-w-96 block truncate">
      {children}
    </span>
  );
}
